import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import WarrantyCertificateModal from '../WarrantyCertificateModal';

export default function ReportsTab({
  selectedMeter,
  meters = [],
  baseUrl,
  Token,
  axiosInstance,
  deviceDetails = {},
  onDownloadCert,
  onDownloadNoc,
  onDownloadRenewal,
  onDownloadMonthlyArchive,
}) {
  const [archives, setArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [modalMeter, setModalMeter] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isWarrantyModalOpen, setIsWarrantyModalOpen] = useState(false);

  // Resolve active meter reliably from props, deviceDetails, or meters array
  const activeMeter = useMemo(() => {
    if (selectedMeter && selectedMeter !== 'All') return selectedMeter;
    if (deviceDetails?.productId && deviceDetails.productId !== 'All') return deviceDetails.productId;
    const match = meters?.find((m) => m.value && m.value !== 'All');
    return match ? match.value : '';
  }, [selectedMeter, deviceDetails, meters]);

  // 1. Fetch Real Archives from Backend
  const fetchArchives = useCallback(async () => {
    const currentMeter = activeMeter;
    if (!currentMeter || currentMeter === 'All' || !baseUrl) return;
    setLoadingArchives(true);
    try {
      const client = axiosInstance || null;
      const headers = Token ? { Authorization: `Bearer ${Token}` } : {};
      const url = `${baseUrl}/archive/list/${currentMeter}`;

      let data = [];
      if (client) {
        const res = await client.get(url, { headers }).catch(() => null);
        data = res?.data?.data || [];
      } else {
        const res = await fetch(url, { headers }).catch(() => null);
        if (res && res.ok) {
          const json = await res.json();
          data = json?.data || [];
        }
      }
      setArchives(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Failed to fetch archives:', err);
    } finally {
      setLoadingArchives(false);
    }
  }, [activeMeter, baseUrl, Token, axiosInstance]);

  useEffect(() => {
    fetchArchives();
  }, [fetchArchives]);

  // 2. Handle File Selection for Upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError('');
      if (!uploadTitle.trim()) {
        const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
        setUploadTitle(baseName);
      }
    }
  };

  // 3. Submit Upload to Backend
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setUploadError('Please select a file to upload.');
      return;
    }
    if (!uploadTitle.trim()) {
      setUploadError('Please provide a title for this archive.');
      return;
    }
    const targetMeter = modalMeter || activeMeter;
    if (!targetMeter || targetMeter === 'All') {
      setUploadError('Please select a valid meter before uploading.');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('title', uploadTitle.trim());
      formData.append('file', selectedFile);

      const url = `${baseUrl}/archive/upload/${targetMeter}`;
      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(Token ? { Authorization: `Bearer ${Token}` } : {}),
      };

      if (axiosInstance) {
        await axiosInstance.post(url, formData, { headers });
      } else {
        const res = await fetch(url, {
          method: 'POST',
          headers: Token ? { Authorization: `Bearer ${Token}` } : undefined,
          body: formData,
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.data || 'Upload failed');
        }
      }

      setIsUploadModalOpen(false);
      setUploadTitle('');
      setSelectedFile(null);
      fetchArchives();
    } catch (err) {
      const msg = err?.response?.data?.data || err?.message || 'Failed to upload archive';
      setUploadError(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const [previewArchiveData, setPreviewArchiveData] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  // Direct Download helper
  const handleDirectDownload = async (archive) => {
    try {
      const url = `${baseUrl}/archive/download/${archive._id || archive.id}`;
      const headers = Token ? { Authorization: `Bearer ${Token}` } : {};
      const ext = (archive.fileName || '').split('.').pop().toLowerCase();
      if (axiosInstance) {
        const res = await axiosInstance.get(url, { responseType: 'blob', headers });
        const blob = new Blob([res.data]);
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', archive.fileName || `${archive.title}.${ext}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      alert('Failed to download file');
    }
  };

  // 4. View / Preview Uploaded Archive
  const handleViewUploadedArchive = async (archive) => {
    const ext = (archive.fileName || '').split('.').pop().toLowerCase();

    // If PDF, open directly in browser tab like NOC and Calibration certificates
    if (ext === 'pdf') {
      try {
        const url = `${baseUrl}/archive/download/${archive._id || archive.id}`;
        const headers = Token ? { Authorization: `Bearer ${Token}` } : {};
        if (axiosInstance) {
          const res = await axiosInstance.get(url, { responseType: 'blob', headers });
          const blob = new Blob([res.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        } else {
          window.open(url, '_blank');
        }
      } catch (err) {
        alert('Failed to view PDF document');
      }
      return;
    }

    // If Image (.png, .jpg, .jpeg, .webp), open directly in browser tab
    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
      try {
        const url = `${baseUrl}/archive/download/${archive._id || archive.id}`;
        const headers = Token ? { Authorization: `Bearer ${Token}` } : {};
        let mime = 'image/jpeg';
        if (ext === 'png') mime = 'image/png';
        else if (ext === 'webp') mime = 'image/webp';

        if (axiosInstance) {
          const res = await axiosInstance.get(url, { responseType: 'blob', headers });
          const blob = new Blob([res.data], { type: mime });
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        } else {
          window.open(url, '_blank');
        }
      } catch (err) {
        alert('Failed to view image document');
      }
      return;
    }

    // If Excel / CSV spreadsheet (.xlsx, .xls, .csv), fetch parsed spreadsheet data and display in viewer modal
    setIsPreviewLoading(true);
    try {
      const url = `${baseUrl}/archive/preview/${archive._id || archive.id}`;
      const headers = Token ? { Authorization: `Bearer ${Token}` } : {};
      let previewResult = null;

      if (axiosInstance) {
        const res = await axiosInstance.get(url, { headers });
        previewResult = res.data?.data;
      } else {
        const res = await fetch(url, { headers });
        const json = await res.json();
        previewResult = json.data;
      }

      if (previewResult && previewResult.sheets && previewResult.sheets.length > 0) {
        setPreviewArchiveData({
          ...previewResult,
          rawArchive: archive,
        });
        setActiveSheetIndex(0);
      } else {
        handleDirectDownload(archive);
      }
    } catch (err) {
      console.warn('Failed to fetch spreadsheet preview:', err);
      handleDirectDownload(archive);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 5. Delete Uploaded Archive via Custom Modal
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const archiveId = deleteTarget._id || deleteTarget.id;
      const url = `${baseUrl}/archive/${archiveId}`;
      const headers = Token ? { Authorization: `Bearer ${Token}` } : {};

      if (axiosInstance) {
        await axiosInstance.delete(url, { headers });
      } else {
        await fetch(url, { method: 'DELETE', headers });
      }
      setDeleteTarget(null);
      fetchArchives();
    } catch (err) {
      console.warn('Failed to delete archive:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Real certificates lookup
  const activeMeterObj = meters.find((m) => (m.value || m.productId) === activeMeter) || {};
  const calibrationCert = deviceDetails.calibrationCertificate || activeMeterObj.calibrationCertificate || {};
  const hasCalibrationCert = Boolean(calibrationCert.fileName || calibrationCert.storedFileName);

  const nocDoc = deviceDetails.nocDetails || activeMeterObj.nocDetails || {};
  const hasNocDoc = Boolean(nocDoc.fileName || nocDoc.storedFileName || nocDoc.nocNumber);

  return (
    <div className="pane active">
      <div className="pane-tools">
        <h2>Reports &amp; documents</h2>
      </div>

      <div className="row two" style={{ marginBottom: '16px' }}>
        {/* Archive Card */}
        <div className="client-card">
          <div className="card-h">
            <h3>Archive</h3>
            <div className="ch-right">
              <button
                className="btn ghost"
                onClick={() => {
                  setUploadTitle('');
                  setSelectedFile(null);
                  setUploadError('');
                  setModalMeter(activeMeter);
                  setIsUploadModalOpen(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                Upload document
              </button>
            </div>
          </div>

          <div id="archiveList">
            {loadingArchives ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#8aa1ad', fontSize: '13px' }}>
                Loading archives...
              </div>
            ) : archives.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#8aa1ad', fontSize: '13px' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.8" style={{ margin: '0 auto 8px', display: 'block' }}>
                  <path d="M4 4h5l2 2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                </svg>
                <div style={{ fontWeight: 600, color: '#64748b' }}>No archived documents uploaded</div>
                <small style={{ color: '#94a3b8' }}>Click "Upload document" to add historical files</small>
              </div>
            ) : null}

            {/* Uploaded Real Archives */}
            {archives.map((a) => (
              <div key={a._id || a.id} className="archive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="f" style={{ flex: 1, minWidth: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1072bb" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                  </svg>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: 600, color: '#0d3b54' }}>{a.title}</span>
                    <small style={{ display: 'block', color: '#8aa1ad', fontSize: '11px', marginTop: '2px' }}>
                      {a.fileSize ? `${a.fileSize} · ` : ''}{moment(a.uploadedAt).format('DD MMM YYYY')}
                    </small>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button
                    className="btn ghost"
                    style={{ padding: '4px 12px', fontSize: '12px' }}
                    onClick={() => handleViewUploadedArchive(a)}
                  >
                    View
                  </button>
                  <button
                    className="btn ghost"
                    style={{ padding: '4px 8px', fontSize: '12px', color: '#e5484d', borderColor: '#fca5a5' }}
                    title="Delete Archive"
                    onClick={() => setDeleteTarget(a)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certificates Card */}
        <div className="client-card">
          <div className="card-h">
            <h3>Certificates</h3>
          </div>

          {/* Warranty Certificate */}
          <div className="archive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="f" style={{ flex: 1, minWidth: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1072bb" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ fontWeight: 600, color: '#0d3b54' }}>Warranty certificate</span>
              </div>
            </div>
            <button
              className="btn ghost"
              style={{ padding: '4px 12px', fontSize: '12px' }}
              onClick={() => setIsWarrantyModalOpen(true)}
            >
              View
            </button>
          </div>

          {hasCalibrationCert && (
            <div className="archive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="f" style={{ flex: 1, minWidth: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1072bb" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ fontWeight: 600, color: '#0d3b54' }}>Calibration certificate</span>
                  <small style={{ display: 'block', color: '#8aa1ad', fontSize: '11px', marginTop: '2px' }}>
                    {calibrationCert.expiryDate
                      ? `Valid to: ${moment(calibrationCert.expiryDate).format('DD/MM/YYYY')}`
                      : calibrationCert.uploadedAt
                        ? `Uploaded: ${moment(calibrationCert.uploadedAt).format('DD/MM/YYYY')}`
                        : 'Official meter calibration certificate'}
                  </small>
                </div>
              </div>
              <button
                className="btn ghost"
                style={{ padding: '4px 12px', fontSize: '12px' }}
                onClick={() => onDownloadCert && onDownloadCert(activeMeter, calibrationCert.fileName)}
              >
                View
              </button>
            </div>
          )}

          {hasNocDoc && (
            <div className="archive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="f" style={{ flex: 1, minWidth: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1072bb" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ fontWeight: 600, color: '#0d3b54' }}>
                    {nocDoc.nocNumber ? `NOC compliance report (${nocDoc.nocNumber})` : 'NOC compliance report'}
                  </span>
                  <small style={{ display: 'block', color: '#8aa1ad', fontSize: '11px', marginTop: '2px' }}>
                    {nocDoc.endDate
                      ? `Valid to: ${moment(nocDoc.endDate).format('DD/MM/YYYY')}`
                      : nocDoc.uploadedAt
                        ? `Uploaded: ${moment(nocDoc.uploadedAt).format('DD/MM/YYYY')}`
                        : 'Official NOC statutory compliance document'}
                  </small>
                </div>
              </div>
              <button
                className="btn ghost"
                style={{ padding: '4px 12px', fontSize: '12px' }}
                onClick={() => onDownloadNoc && onDownloadNoc(activeMeter, nocDoc.fileName)}
              >
                View
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload Archive Modal */}
      {isUploadModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(13, 59, 84, 0.45)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => !isUploading && setIsUploadModalOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '28px',
              width: '100%',
              maxWidth: '480px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              border: '1px solid #eef2f5',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#0d3b54', fontWeight: 700 }}>Upload Archive Document</h3>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  color: '#8aa1ad',
                  cursor: 'pointer',
                }}
                onClick={() => !isUploading && setIsUploadModalOpen(false)}
              >
                ✕
              </button>
            </div>

            {meters && meters.filter((m) => m.value && m.value !== 'All').length > 1 ? (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0d3b54', marginBottom: '6px' }}>
                  Target Meter
                </label>
                <select
                  value={modalMeter || activeMeter}
                  onChange={(e) => setModalMeter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #dbe4ea',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#0d3b54',
                    background: '#fff',
                  }}
                >
                  {meters
                    .filter((m) => m.value && m.value !== 'All')
                    .map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label || m.name || m.value}
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                Upload historical telemetry, monthly exports, or legacy reports for meter <b>{modalMeter || activeMeter}</b>.
              </p>
            )}

            <form onSubmit={handleUploadSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0d3b54', marginBottom: '6px' }}>
                  Archive Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. August 2026 Telemetry"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #dbe4ea',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#0d3b54',
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#0d3b54', marginBottom: '6px' }}>
                  Attachment File
                </label>
                <div
                  style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '10px',
                    padding: '20px',
                    textAlign: 'center',
                    background: '#f8fafc',
                    cursor: 'pointer',
                  }}
                  onClick={() => document.getElementById('archiveFileInput')?.click()}
                >
                  <input
                    id="archiveFileInput"
                    type="file"
                    style={{ display: 'none' }}
                    accept=".xlsx,.xls,.csv,.pdf,.zip,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  {selectedFile ? (
                    <div>
                      <div style={{ fontWeight: 600, color: '#1072bb', fontSize: '14px' }}>{selectedFile.name}</div>
                      <small style={{ color: '#64748b' }}>{(selectedFile.size / 1024).toFixed(0)} KB</small>
                    </div>
                  ) : (
                    <div>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8" style={{ margin: '0 auto 8px' }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                      <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                        Click to select an Excel, CSV, PDF, or ZIP file
                      </div>
                      <small style={{ color: '#94a3b8' }}>Max file size: 50MB</small>
                    </div>
                  )}
                </div>
              </div>

              {uploadError && (
                <div style={{ padding: '10px', background: '#fef2f2', color: '#ef4444', borderRadius: '8px', fontSize: '12px', marginBottom: '16px' }}>
                  {uploadError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={isUploading}
                  onClick={() => setIsUploadModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={isUploading}
                  style={{ minWidth: '120px' }}
                >
                  {isUploading ? 'Uploading...' : 'Save Archive'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(13, 59, 84, 0.45)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => !isDeleting && setDeleteTarget(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '420px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              border: '1px solid #eef2f5',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fee2e2',
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
              </svg>
            </div>

            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0d3b54', fontWeight: 700 }}>
              Delete Archive Document?
            </h3>

            <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0', lineHeight: 1.5 }}>
              Are you sure you want to permanently delete <b>{deleteTarget.title}</b>?
              <span style={{ display: 'block', color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>
                {deleteTarget.fileName}
              </span>
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button
                type="button"
                className="btn ghost"
                disabled={isDeleting}
                onClick={() => setDeleteTarget(null)}
                style={{ minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                style={{
                  minWidth: '100px',
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Spreadsheet / Document Viewer Modal */}
      {previewArchiveData && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(13, 59, 84, 0.65)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '24px',
          }}
          onClick={() => setPreviewArchiveData(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '1100px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 24px',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: '#e0f2fe',
                    color: '#0284c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M16 13H8M16 17H8M10 9H8" />
                  </svg>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0d3b54' }}>
                    {previewArchiveData.title}
                  </h3>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    {previewArchiveData.fileSize ? `${previewArchiveData.fileSize} · ` : ''}
                    Uploaded: {moment(previewArchiveData.uploadedAt).format('DD MMM YYYY')}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  className="btn ghost"
                  style={{ padding: '6px 14px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => handleDirectDownload(previewArchiveData.rawArchive)}
                  title="Download Original File"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Download File
                </button>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    lineHeight: 1,
                  }}
                  onClick={() => setPreviewArchiveData(null)}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Sheets Navigation Bar (if multi-sheet) */}
            {previewArchiveData.sheets && previewArchiveData.sheets.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  padding: '8px 20px',
                  background: '#f1f5f9',
                  borderBottom: '1px solid #e2e8f0',
                  overflowX: 'auto',
                }}
              >
                {previewArchiveData.sheets.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSheetIndex(idx)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: activeSheetIndex === idx ? 700 : 500,
                      background: activeSheetIndex === idx ? '#fff' : 'transparent',
                      color: activeSheetIndex === idx ? '#0284c7' : '#64748b',
                      boxShadow: activeSheetIndex === idx ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    📄 {s.name}
                  </button>
                ))}
              </div>
            )}

            {/* Table Grid View */}
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                padding: '20px',
                background: '#fff',
              }}
            >
              {(() => {
                const currentSheet = previewArchiveData.sheets?.[activeSheetIndex] || previewArchiveData.sheets?.[0];
                const rows = currentSheet?.data || [];
                if (rows.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      No data found in this sheet.
                    </div>
                  );
                }

                const maxCols = rows.reduce((max, r) => (Array.isArray(r) ? Math.max(max, r.length) : max), 0);

                return (
                  <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '12px',
                        color: '#1e293b',
                        fontFamily: 'inherit',
                      }}
                    >
                      <tbody>
                        {rows.map((row, rIdx) => {
                          const isHeader = rIdx === 0;
                          return (
                            <tr
                              key={rIdx}
                              style={{
                                background: isHeader ? '#f8fafc' : rIdx % 2 === 0 ? '#ffffff' : '#fcfdfd',
                                borderBottom: '1px solid #e2e8f0',
                              }}
                            >
                              <td
                                style={{
                                  width: '36px',
                                  textAlign: 'center',
                                  padding: '6px 8px',
                                  color: '#94a3b8',
                                  fontSize: '11px',
                                  background: '#f8fafc',
                                  borderRight: '1px solid #e2e8f0',
                                  userSelect: 'none',
                                }}
                              >
                                {rIdx + 1}
                              </td>
                              {Array.from({ length: maxCols }).map((_, cIdx) => {
                                const cellVal = Array.isArray(row) ? row[cIdx] : '';
                                return (
                                  <td
                                    key={cIdx}
                                    style={{
                                      padding: '8px 12px',
                                      borderRight: '1px solid #f1f5f9',
                                      fontWeight: isHeader ? 700 : 400,
                                      color: isHeader ? '#0d3b54' : '#334155',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {cellVal !== undefined && cellVal !== null ? String(cellVal) : ''}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 24px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc',
                fontSize: '12px',
                color: '#64748b',
              }}
            >
              <span>
                Sheet: <b>{previewArchiveData.sheets?.[activeSheetIndex]?.name || 'Data'}</b> (
                {previewArchiveData.sheets?.[activeSheetIndex]?.data?.length || 0} rows)
              </span>
              <button
                className="btn ghost"
                style={{ padding: '4px 16px', fontSize: '12px' }}
                onClick={() => setPreviewArchiveData(null)}
              >
                Close Viewer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warranty Certificate Modal */}
      <WarrantyCertificateModal
        isOpen={isWarrantyModalOpen}
        onClose={() => setIsWarrantyModalOpen(false)}
        productId={activeMeter}
        deviceDetails={deviceDetails}
        activeMeterObj={activeMeterObj}
        baseUrl={baseUrl}
        Token={Token}
        axiosInstance={axiosInstance}
      />
    </div>
  );
}

